/**
 * Authorization Code flow with PKCE.
 *
 * No client secret exists — a public client cannot keep one — so PKCE is what
 * stops an intercepted authorization code being redeemed by anyone else. The
 * verifier never leaves this browser; only its hash does.
 */

const VERIFIER_KEY = 'qim.pkce.verifier'
const STATE_KEY = 'qim.pkce.state'
const RETURN_KEY = 'qim.pkce.return-to'
const AUTO_ATTEMPT_KEY = 'qim.pkce.auto-attempt'

const base64Url = (bytes: Uint8Array): string => {
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/** 32 bytes of CSPRNG output, base64url encoded — 43 characters, within spec. */
export const randomUrlSafe = (bytes = 32): string =>
  base64Url(crypto.getRandomValues(new Uint8Array(bytes)))

export const challengeFor = async (verifier: string): Promise<string> => {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))
  return base64Url(new Uint8Array(digest))
}

export interface PendingAuth {
  readonly verifier: string
  readonly state: string
  /**
   * The path and query being viewed when the sign-in began, so the round trip
   * lands back on it (IM-170). Validated on the way in *and* out — see
   * `safeReturnPath`.
   */
  readonly returnTo?: string
}

/**
 * A same-origin relative path, or nothing.
 *
 * This value comes back out of sessionStorage and is handed to
 * `history.replaceState`, so a value that could name another origin would be
 * an open redirect with our sign-in in front of it. `//evil.com` is the
 * classic: it starts with `/` and is protocol-relative. `/\evil.com` is the
 * same trick, because browsers read a backslash as a slash in a URL. Resolving
 * against a throwaway origin and requiring that origin back is the check that
 * does not depend on having thought of every spelling; the prefix rules are
 * there so the obvious cases fail without relying on the URL parser alone.
 */
export const safeReturnPath = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  if (!value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) return undefined
  // The URL parser strips tabs and newlines, so `/<TAB>/evil.com` passes the
  // prefix check and then resolves as `//evil.com`. Refuse control characters
  // outright rather than trusting the resolution below to have caught it.
  for (const char of value) {
    const code = char.charCodeAt(0)
    if (code < 0x20 || code === 0x7f) return undefined
  }
  const probe = 'https://return-to.invalid'
  let resolved: URL
  try {
    resolved = new URL(value, probe)
  } catch {
    return undefined
  }
  return resolved.origin === probe ? value : undefined
}

/**
 * Kept in sessionStorage rather than localStorage: the flow is meaningful only
 * within one tab's session, and a leftover verifier in localStorage would
 * outlive the sign-in it belongs to.
 */
export const rememberPending = (storage: Storage, pending: PendingAuth): void => {
  storage.setItem(VERIFIER_KEY, pending.verifier)
  storage.setItem(STATE_KEY, pending.state)
  const returnTo = safeReturnPath(pending.returnTo)
  if (returnTo === undefined) storage.removeItem(RETURN_KEY)
  else storage.setItem(RETURN_KEY, returnTo)
}

/**
 * Reads and *clears* the pending flow. Single use: a verifier that survives its
 * exchange could be replayed against a second intercepted code.
 */
export const takePending = (storage: Storage): PendingAuth | undefined => {
  const verifier = storage.getItem(VERIFIER_KEY)
  const state = storage.getItem(STATE_KEY)
  // Checked again on the way out: sessionStorage is writable by anything that
  // runs in this origin, so what was validated going in is not proof of what
  // comes back.
  const returnTo = safeReturnPath(storage.getItem(RETURN_KEY))
  storage.removeItem(VERIFIER_KEY)
  storage.removeItem(STATE_KEY)
  storage.removeItem(RETURN_KEY)
  if (verifier === null || state === null) return undefined
  return returnTo === undefined ? { verifier, state } : { verifier, state, returnTo }
}

/**
 * How long an automatic sign-in attempt suppresses the next one (IM-170).
 *
 * A healthy round trip through the hosted UI is a second or two. If this tab
 * is back here without a token inside this window, the last attempt did not
 * work, and redirecting again would bounce between two origins forever.
 */
export const AUTO_ATTEMPT_WINDOW_MS = 2 * 60_000

/**
 * The loop guard: a timestamp, never a token.
 *
 * Beside the verifier because it has the same lifetime — this tab's session —
 * and the same reason not to be in localStorage: a marker there would stop
 * every other tab signing in silently too.
 */
export const recordAutoAttempt = (storage: Storage, now: number): void => {
  storage.setItem(AUTO_ATTEMPT_KEY, String(now))
}

export const clearAutoAttempt = (storage: Storage): void => {
  storage.removeItem(AUTO_ATTEMPT_KEY)
}

export const recentAutoAttempt = (
  storage: Storage,
  now: number,
  windowMs: number = AUTO_ATTEMPT_WINDOW_MS,
): boolean => {
  const at = Number(storage.getItem(AUTO_ATTEMPT_KEY))
  // `Number(null)` is 0, which is the distant past — no attempt recorded.
  // A future timestamp (a clock moved back) counts as recent, which errs
  // towards a click rather than towards a loop.
  return Number.isFinite(at) && at > 0 && now - at < windowMs
}

export type CallbackResult =
  | { readonly kind: 'code'; readonly code: string }
  | { readonly kind: 'error'; readonly message: string }
  | { readonly kind: 'none' }

/**
 * Reads the callback parameters and checks `state`.
 *
 * A mismatched state means the response did not originate from a flow this tab
 * started, which is what makes it a CSRF defence rather than decoration. It is
 * treated as an error, never ignored.
 */
export const readCallback = (search: string, expectedState: string | undefined): CallbackResult => {
  const params = new URLSearchParams(search)
  const error = params.get('error')
  if (error !== null) {
    return { kind: 'error', message: params.get('error_description') ?? error }
  }

  const code = params.get('code')
  if (code === null) return { kind: 'none' }

  const state = params.get('state')
  if (expectedState === undefined || state !== expectedState) {
    return { kind: 'error', message: 'Sign-in state did not match. Start again.' }
  }

  return { kind: 'code', code }
}

export interface AuthConfig {
  readonly domain: string
  readonly clientId: string
  readonly redirectUri: string
  readonly providerName: string
}

export const authorizeUrl = (config: AuthConfig, challenge: string, state: string): string => {
  const params = new URLSearchParams({
    client_id: config.clientId,
    response_type: 'code',
    scope: 'openid email profile',
    redirect_uri: config.redirectUri,
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
    // Straight to Identity Center — the pool has no other provider, so a chooser
    // would be a dead end.
    identity_provider: config.providerName,
  })
  return `${config.domain}/oauth2/authorize?${params.toString()}`
}

export interface TokenSet {
  /**
   * The **id** token, not the access token.
   *
   * API Gateway's JWT authorizer validates `aud`, and the application needs
   * `email` — a Cognito access token carries neither. Sending the access token
   * produces a 401 that looks like a permissions problem.
   */
  readonly idToken: string
  readonly expiresAt: number
  /**
   * What renews the id token without a redirect (IM-170). Held in memory with
   * the rest of the set and never written anywhere: it is the longest-lived of
   * the three, so it is the one most worth not leaving in storage.
   */
  readonly refreshToken?: string
}

/**
 * Reads a token endpoint response.
 *
 * Shared by the code exchange and the refresh, which answer in the same shape
 * except that Cognito's refresh response carries no new `refresh_token` — so
 * the caller's existing one is the fallback, not an absence.
 */
const readTokenResponse = async (
  response: Response,
  what: string,
  now: () => number,
  fallbackRefreshToken?: string,
): Promise<TokenSet> => {
  if (!response.ok) {
    throw new Error(`${what} failed (${String(response.status)})`)
  }

  const body: unknown = await response.json()
  if (typeof body !== 'object' || body === null) throw new Error('Token response was not an object')
  const idToken = (body as Record<string, unknown>)['id_token']
  const expiresIn = (body as Record<string, unknown>)['expires_in']
  const refreshToken = (body as Record<string, unknown>)['refresh_token']
  if (typeof idToken !== 'string') throw new Error('Token response carried no id_token')

  const kept = typeof refreshToken === 'string' ? refreshToken : fallbackRefreshToken
  const expiresAt = now() + (typeof expiresIn === 'number' ? expiresIn : 900) * 1000
  return kept === undefined ? { idToken, expiresAt } : { idToken, expiresAt, refreshToken: kept }
}

export const exchangeCode = async (
  config: AuthConfig,
  code: string,
  verifier: string,
  now: () => number = Date.now,
  fetchImpl: typeof fetch = fetch,
): Promise<TokenSet> => {
  const response = await fetchImpl(`${config.domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: config.clientId,
      code,
      redirect_uri: config.redirectUri,
      code_verifier: verifier,
    }).toString(),
  })

  return readTokenResponse(response, 'Token exchange', now)
}

/**
 * Renews the tokens with the refresh token (IM-170).
 *
 * A public client, so no secret and no `Authorization` header — the same
 * endpoint and form encoding as the code exchange. Cognito answers with a new
 * id and access token and *no* refresh token, so the one passed in is carried
 * over; losing it would make the second renewal impossible.
 */
export const refreshTokens = async (
  config: AuthConfig,
  refreshToken: string,
  now: () => number = Date.now,
  fetchImpl: typeof fetch = fetch,
): Promise<TokenSet> => {
  const response = await fetchImpl(`${config.domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: config.clientId,
      refresh_token: refreshToken,
    }).toString(),
  })
  return readTokenResponse(response, 'Token renewal', now, refreshToken)
}
